import React, { useEffect, useRef, useCallback } from 'react';
import { defineMessages, injectIntl } from 'react-intl-next';
import type { WrappedComponentProps } from 'react-intl-next';

import Link from '@atlaskit/link';
import { Text } from '@atlaskit/primitives/compiled';
import type { EditorActions } from '@atlaskit/editor-core';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { filter } from '@atlaskit/adf-utils/traverse';
import { globalMediaEventEmitter } from '@atlaskit/media-client';

import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { containsNetworkError } from '@confluence/network';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { ContentAttachmentsQuery } from './ContentAttachmentsQuery.graphql.ts';
import type {
	ContentAttachmentsQuery as ContentAttachmentsQueryType,
	ContentAttachmentsQueryVariables,
} from './__types__/ContentAttachmentsQuery.ts';

export type SameFileNameWarningFlagComponentProps = {
	editorActions: EditorActions;
	flags: FlagsStateContainer;
	contentId: string;
} & WrappedComponentProps;

const i18n = defineMessages({
	sameFileNameTitle: {
		id: 'editor-features.same.file.name.title',
		defaultMessage: '“{file}” already exists',
		description: 'Warns the user that the file uploaded already exists on the editor',
	},
	sameFileNameDescription: {
		id: 'editor-features.same.file.name.description',
		defaultMessage:
			'The file you chose has the same name as an existing attachment, and the new one will replace the existing one.',
		description:
			'Warns the user that the file uploaded will replace existing files on the editor with the same name',
	},
	sameFileNameLearnMore: {
		id: 'editor-features.same.file.name.learn.more',
		defaultMessage: 'Learn more about attachments.',
		description: 'Link that opens a new tab to a page about editor attachments',
	},
});

const ATTACHMENTS_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/manage-uploaded-files/';

const SameFileNameWarningFlagComponent: React.ComponentType<
	WrappedComponentProps & SameFileNameWarningFlagComponentProps
> = ({ editorActions, flags, contentId, intl }: SameFileNameWarningFlagComponentProps) => {
	// [file id]: file name
	const attachmentsMapRef = useRef({});

	/**
	 * parses the editor adf to get media nodes
	 * @returns mediaNodes[]
	 */
	const getMediaNodesFromAdf = useCallback(async (): Promise<ADFEntity[]> => {
		const adf = await editorActions.getValue();
		const mediaNodes = filter(
			adf,
			(node) =>
				node.type === 'media' && !!node?.attrs?.['collection'] && node.attrs?.type !== 'external',
		);
		return mediaNodes;
	}, [editorActions]);

	/**
	 * sets a map of attachments on the content
	 */
	const getAttachmentsMap = useCallback(async (): Promise<void> => {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const { data }: { data: ContentAttachmentsQueryType | null } = await getApolloClient()
			.query({
				query: ContentAttachmentsQuery,
				variables: {
					contentId,
					status: ['current', 'draft'],
				} as ContentAttachmentsQueryVariables,
				fetchPolicy: 'network-only', // should get the latest ids
			})
			.catch((e) => {
				if (isUnauthorizedError(e) || containsNetworkError(e)) {
					markErrorAsHandled(e);
				} else {
					getMonitoringClient().submitError(e, {
						attribution: Attribution.PAGE_MODES,
					});
				}
				return { data: null };
			});
		if (data === null) {
			return;
		}
		const attachmentsMap =
			data.content?.edges?.[0]?.node?.attachments?.nodes?.reduce((acc, curr) => {
				const fileId = curr?.extensions?.find((i) => i?.key === 'fileId')?.value;
				if (curr?.title && fileId) {
					acc[fileId] = curr.title;
				}
				return acc;
			}, {}) || {};
		Object.assign(attachmentsMapRef.current, attachmentsMap);
	}, [contentId]);

	/**
	 * @param filename
	 */
	const showSameFileNameFlag = useCallback(
		(filename: string) => {
			void flags.showWarningFlag({
				id: `file-with-same-name-${filename}`,
				title: intl.formatMessage(i18n.sameFileNameTitle, {
					file: filename,
				}),
				description: (
					<Text as="p">
						{intl.formatMessage(i18n.sameFileNameDescription)}
						&nbsp;
						{fg('dst-a11y__replace-anchor-with-link__page-experienc') ? (
							<Link href={ATTACHMENTS_LINK} target="_blank">
								{intl.formatMessage(i18n.sameFileNameLearnMore)}
							</Link>
						) : (
							// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
							<a href={ATTACHMENTS_LINK} target="_blank">
								{intl.formatMessage(i18n.sameFileNameLearnMore)}
							</a>
						)}
					</Text>
				),
			});
		},
		[flags, intl],
	);

	/**
	 * globalMediaEventEmitter callback, compares uploaded file name to file names on the editor
	 * @param uploadedFileDetails parameter from globalMediaEventEmitter
	 */
	const checkForDuplicateFileNames = useCallback(
		async (uploadedFileDetails: any): Promise<void> => {
			const { name: uploadedFileName, id: uploadedFileId } = uploadedFileDetails;

			const mediaNodes = await getMediaNodesFromAdf();

			for (const mediaNode of mediaNodes) {
				const mediaNodeFileId = mediaNode?.attrs?.id;

				// no id and do not check self
				if (!mediaNodeFileId || mediaNodeFileId === uploadedFileId) {
					continue;
				}

				if (attachmentsMapRef.current[mediaNodeFileId] === uploadedFileName) {
					showSameFileNameFlag(uploadedFileName);
					break;
				}
			}

			attachmentsMapRef.current[uploadedFileId] = uploadedFileName;
		},
		[getMediaNodesFromAdf, showSameFileNameFlag],
	);

	useEffect(() => {
		globalMediaEventEmitter.on('file-added', checkForDuplicateFileNames);

		return () => {
			globalMediaEventEmitter.off('file-added', checkForDuplicateFileNames);
		};
	}, [checkForDuplicateFileNames]);

	useEffect(() => {
		void getAttachmentsMap();
	}, [getAttachmentsMap]);

	return null;
};

export const SameFileNameWarningFlag = withFlags(injectIntl(SameFileNameWarningFlagComponent));
